import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { addItem, updateItem } from "../../store/slices/defectSlice";
import { useFormik } from "formik";
import { Defect } from "../../types/defect";
import { useMemo, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { getDefectEditableFields } from "../../utils/defectTableHelper";
import { FormDropdown } from "../Shared/FormDropdown";
import { FormInputTextarea } from "../Shared/FormInputTextarea";
import { addDefect, updateDefect } from "../../services/defectService";
import { GradingAreaMapping } from "../../config/GradingAreaMapping";
import { FormEstimateDefectCost } from "./FormEstimateDefectCost";
import { FormMultiSelect } from "../Shared/FormMultiSelect";
import {
    selectInspectionEnableLowPriorityDefects,
    selectPhotoFolders
} from "../../store/selectors/inspectionSelectors";
import { selectDefectCodes } from "../../store/selectors/defectSelectors";
import { FormDefectCode } from "./FormDefectCode";

type Props = {
    defect: Defect;
    onCancel?: () => void;
};

export const DefectForm = (props: Props) => {
    const { defect, onCancel } = props;

    const dispatch = useAppDispatch();

    const photoFolders = useAppSelector(selectPhotoFolders);
    const enableLowPriorityDefects = useAppSelector(
        selectInspectionEnableLowPriorityDefects
    );
    const defectCodes = useAppSelector(selectDefectCodes);

    const [loading, setLoading] = useState(false);

    const getCodeCategory = (typeCode: string) => {
        const defectCode = defectCodes.find(dc => dc.code === typeCode);
        return defectCode?.category?.toLowerCase() || "general";
    };

    const formik = useFormik<Defect>({
        initialValues: defect,
        validate: (data) => {
            let errors: Record<string, unknown> = {};

            if (data.typeCode && !defectCodes.find(dc => dc.code === data.typeCode)) {
                errors.typeCode = "Invalid defect code.";
            }

            if (!data.priority) {
                errors.priority = "Priority is required.";
            }

            if (!data.description) {
                errors.description = "Description is required.";
            }

            if (!data.recommendations) {
                errors.recommendations = "Recommendations is required.";
            }

            return errors;
        },
        onSubmit: async (data) => {
            setLoading(true);

            if (defect.defectId) {
                const updated = await updateDefect(data);

                if (updated) {
                    dispatch(updateItem(data));
                }
            } else {
                const added = await addDefect(data);

                if (added) {
                    dispatch(addItem(added));
                }
            }

            setLoading(false);
        }
    });

    const editableFields = useMemo(
        () => getDefectEditableFields(Boolean(defect.key && defect.rawAnswer)),
        []
    );

    useEffect(() => {
        if (formik.values.typeCode) {
            const category = getCodeCategory(formik.values.typeCode);
            if (category && formik.values.category !== category) {
                formik.setFieldValue("category", category);
            }
        }
    }, [formik.values.typeCode, defectCodes]);

    const gradingAreaOptions = useMemo(
        () =>
            Object.keys(GradingAreaMapping).map((key) => ({
                label: GradingAreaMapping[key],
                value: key
            })),
        []
    );

    const categoryOptions = useMemo(() => {
        const uniqueCategories = Array.from(new Set(defectCodes.map(dc => dc.category).filter(Boolean)));
        return uniqueCategories.map(cat => ({
            label: cat.charAt(0).toUpperCase() + cat.slice(1),
            value: cat.toLowerCase()
        }));
    }, [defectCodes]);

    const priorityOptions = useMemo(() => {
        const options = [
            { label: "High", value: 2 },
            { label: "Medium", value: 3 }
        ];

        if (enableLowPriorityDefects) {
            options.push({ label: "Low", value: 5 });
        }

        return options;
    }, [enableLowPriorityDefects]);

    const photoFolderOptions = useMemo(
        () =>
            photoFolders.map((photoFolder) => ({
                label: photoFolder.Name,
                value: photoFolder.Id.toString()
            })),
        []
    );

    return (
        <div className="flex pt-2 px-5">
            <form
                onSubmit={formik.handleSubmit}
                className="flex w-full flex-column"
                style={{ '--field-margin': '0.5rem' } as React.CSSProperties}
            >
                <style>{`
                    form .field { margin-bottom: 0.25rem; }
                    form .grid > * { padding: 0.3rem 0.5rem !important; }
                    form .grid { padding-top: 0.5rem; }
                `}</style>
                <div className="grid">
                    <div className="col-3 flex align-items-center">
                        <label>Description</label>
                    </div>
                    <div className="col-9">
                        <FormInputTextarea
                            disabled={!editableFields.includes("description")}
                            formik={formik}
                            value="description"
                            label=""
                        />
                    </div>

                    <div className="col-3 flex align-items-center">
                        <label>Category</label>
                    </div>
                    <div className="col-9">
                        <FormDropdown
                            disabled={!editableFields.includes("category")}
                            formik={formik}
                            value="category"
                            label=""
                            options={categoryOptions}
                        />
                    </div>
                
                    <div className="col-3 flex align-items-center">
                        <label>Defect Code</label>
                    </div>
                    <div className="col-9">
                        <FormDefectCode
                            disabled={!editableFields.includes("typeCode")}
                            formik={formik}
                            category={categoryOptions.find(opt => opt.value === formik.values.category)?.label}
                            description={formik.values.description || undefined}
                        />
                    </div>

                    <div className="col-12">
                        <Divider />
                    </div>

                    <div className="col-3 flex align-items-center">
                        <label>Report section(s)</label>
                    </div>
                    <div className="col-9">
                        <FormMultiSelect
                            disabled={!editableFields.includes("gradingArea")}
                            formik={formik}
                            value="gradingArea"
                            label=""
                            options={gradingAreaOptions}
                            splitter=";"
                        />
                    </div>

                    <div className="col-3 flex align-items-center">
                        <label>Priority</label>
                    </div>
                    <div className="col-9">
                        <FormDropdown
                            disabled={!editableFields.includes("priority")}
                            formik={formik}
                            value="priority"
                            label=""
                            options={priorityOptions}
                        />
                    </div>

                    <div className="col-3 flex align-items-center">
                        <label>Recommendations</label>
                    </div>
                    <div className="col-9">
                        <FormInputTextarea
                            disabled={!editableFields.includes("recommendations")}
                            formik={formik}
                            value="recommendations"
                            label=""
                        />
                    </div>

                    <div className="col-3 flex align-items-center">
                        <label>Photo Folder(s)</label>
                    </div>
                    <div className="col-9">
                        <FormMultiSelect
                            disabled={!editableFields.includes("photoFolder")}
                            formik={formik}
                            value="photoFolder"
                            label=""
                            options={photoFolderOptions}
                            splitter=";"
                        />
                    </div>

                    <div className="col-3 flex align-items-center">
                        <label>Estimated Cost</label>
                    </div>
                    <div className="col-9">
                        <FormEstimateDefectCost
                            disabled={
                                !editableFields.includes("estimateDefectCostRangeId")
                            }
                            formik={formik}
                            value="estimateDefectCostRangeId"
                        />
                    </div>
                </div>

                <div className="flex gap-8 mt-4 justify-content-center px-8">
                    <Button
                        type="button"
                        label="Cancel"
                        className="flex-1 p-button-text"
                        onClick={onCancel}
                    />
                    <Button
                        icon={loading ? "pi pi-spin pi-spinner" : undefined}
                        type="submit"
                        label={loading ? undefined : "Submit"}
                        disabled={loading || !formik.isValid || !formik.dirty}
                        className="flex-1"
                    />
                </div>
            </form>
        </div>
    );
};
